import { apiClient, getUserId } from "./authService";

export type PromoData = { [key: string]: any };
type AppointmentQueryOptions = {
  viewType?: "day" | "week" | "month";
  date?: string;
  page?: number;
  size?: number;
};

export const validateAppointment = async (AppointmentData: PromoData): Promise<any> => {
  try {
    const response = await apiClient.post("/business/validate", AppointmentData);
    return response.data as any[];
  } catch (error) {
    throw error;
  }
};
export const getAppointmentsByBusiness = async (
  businessId: string,
  options?: AppointmentQueryOptions
): Promise<any[]> => {
  const params = new URLSearchParams();
  if (options?.viewType) params.append('viewType', options.viewType);
  if (options?.date) params.append('date', options.date);
  if (typeof options?.page === "number" && Number.isFinite(options.page)) {
    params.append("page", String(options.page));
  }
  if (typeof options?.size === "number" && Number.isFinite(options.size)) {
    params.append("size", String(options.size));
  }

  const query = params.toString() ? `?${params.toString()}` : '';

  const response = await apiClient.get(
    `/appointments/business/${businessId}${query}`
  );

  return response.data as any[];
};
export const getAppointmentsByUnit = async (
  unitId: string,
  options?: AppointmentQueryOptions
): Promise<any[]> => {
  const params = new URLSearchParams();
  if (options?.viewType) params.append('viewType', options.viewType);
  if (options?.date) params.append('date', options.date);
  if (typeof options?.page === "number" && Number.isFinite(options.page)) {
    params.append("page", String(options.page));
  }
  if (typeof options?.size === "number" && Number.isFinite(options.size)) {
    params.append("size", String(options.size));
  }

  const query = params.toString() ? `?${params.toString()}` : '';

  const response = await apiClient.get(
    `/appointments/unit/${unitId}${query}`
  );

  return response.data as any[];
};


/**
 * The coach calendar. Same shape as the unit endpoint, projected class tiles
 * included — `viewType` must bound the window or the rule-generated tiles are
 * silently left out, and they only ever ride along on `page=0`.
 *
 * No caller yet — the only calendar here is the admin court grid, which shows
 * every coach's classes already. This is groundwork for the coach-facing
 * calendar that comes later; when it lands, page it the same way the court
 * grid does (`collectPagedAppointments` in Appointment.tsx), because projected
 * tiles ride along on page 0 only and arrive out of order.
 */
export const getAppointmentsByResource = async (
  resourceId: string,
  options?: AppointmentQueryOptions
): Promise<any[]> => {
  const params = new URLSearchParams();
  if (options?.viewType) params.append("viewType", options.viewType);
  if (options?.date) params.append("date", options.date);
  if (typeof options?.page === "number" && Number.isFinite(options.page)) {
    params.append("page", String(options.page));
  }
  if (typeof options?.size === "number" && Number.isFinite(options.size)) {
    params.append("size", String(options.size));
  }

  const query = params.toString() ? `?${params.toString()}` : "";

  const response = await apiClient.get(
    `/appointments/resource/${resourceId}${query}`
  );

  return response.data as any[];
};

export type UnavailableSlotSource = "APPOINTMENT" | "BLOCK" | "CLASS";

export interface UnavailableSlot {
  id: string;
  source: UnavailableSlotSource | string;
  start_at: string;
  end_at: string;
  status?: string;
  /** Present on CLASS slots only. */
  class_id?: string;
  class_name?: string;
  /** True when the class slot is projected from the rule, not a real row. */
  is_virtual?: boolean;
  [key: string]: any;
}

/**
 * Everything occupying a unit in the window — real appointments, admin blocks,
 * and now classes. A projected class occupies its court just as firmly as a
 * booked row, so `source: "CLASS"` entries have to be treated as taken;
 * skipping only APPOINTMENT and BLOCK offers times a class is already using.
 */
export const getUnavailableSlots = async (
  unitId: string,
  options?: AppointmentQueryOptions & { start?: string; end?: string }
): Promise<UnavailableSlot[]> => {
  const response = await apiClient.get(
    `/appointments/unit/${unitId}/unavailable-slots`,
    {
      params: {
        ...(options?.viewType ? { viewType: options.viewType } : {}),
        ...(options?.date ? { date: options.date } : {}),
        ...(options?.start ? { start: options.start } : {}),
        ...(options?.end ? { end: options.end } : {}),
      },
    }
  );
  const body = (response.data as any)?.data ?? response.data;
  const slots = body?.unavailable_slots ?? body?.slots ?? body;
  return Array.isArray(slots) ? (slots as UnavailableSlot[]) : [];
};

export const createAppointment = async (
  appointmentData: PromoData | FormData
): Promise<any> => {
  try {
    if (appointmentData instanceof FormData) {
      console.log("Appointment Data FormData:", appointmentData);
      const response = await apiClient.post("/appointments/create", appointmentData);
      return response.data;
    } else {
      const response = await apiClient.post("/appointments/create", appointmentData);
      return response.data;
    }
  } catch (error) {
    throw error;
  }
};

export const updateAppointment = async (
  id: string,
  appointmentData: PromoData,
  scope?: "ALL"
): Promise<any> => {
  try {
    const status = appointmentData?.status?.toString?.();

    if (status && status.toUpperCase() === "CANCELLED") {
      const reason = (appointmentData as any)?.reason ?? (appointmentData as any)?.cancelReason;
      return await cancelAppointment(
        id,
        typeof reason === "string" ? reason : undefined,
        scope
      );
    }

    try {
      const response = await apiClient.patch(`/appointments/update/${id}`, appointmentData);
      return response.data;
    } catch (error: any) {
      // The booking carries a loyalty reward that the new court/service does not
      // cover. The editor decides: cancel the edit, or proceed without the reward
      // (the backend then gives the client the reward's points back).
      const data = error?.response?.data;
      if (
        data?.code !== "REWARD_NOT_AVAILABLE_FOR_SERVICE" ||
        appointmentData?.drop_loyalty_reward
      ) {
        throw error;
      }

      const proceed = window.confirm(
        `${data.message}\n\nOK = proceed without the reward\nCancel = cancel the edit`,
      );
      if (!proceed) {
        // Callers already toast the message and revert the calendar on errors.
        throw new Error("Edit cancelled. The booking keeps its loyalty reward.");
      }

      const response = await apiClient.patch(`/appointments/update/${id}`, {
        ...appointmentData,
        drop_loyalty_reward: true,
      });
      return response.data;
    }
  } catch (error) {
    throw error;
  }
};

export const deleteAppointment = async (
  appointmentId: string,
  scope?: "ALL" | "UPCOMING",
  date?: string
) => {
  const res = await apiClient.delete(`/appointments/delete/${appointmentId}`, {
    params: {
      ...(scope ? { scope } : {}),
      ...(date ? { date } : {}),
    },
  });
  return res.data;
};


export const cancelAppointment = async (
  appointmentId: string,
  reason?: string,
  scope?: string
) => {
  const params = scope ? { scope } : {};
  const res = await apiClient.patch(`/appointments/${appointmentId}/cancel`, {
    reason,
  }, { params });
  return res.data;
};

export const getAppointmentsForClient = async (
  clientId: string,
  opts?: { from?: string; to?: string; page?: number; size?: number }
) => {
  const { from, to, page, size } = opts || {};
  try {
    const res = await apiClient.get(`/appointments/client/${clientId}`, {
      params: {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(page !== undefined ? { page } : {}),
        ...(size !== undefined ? { size } : {}),
      },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};
